import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { tokens } from "../../../../theme";
import AnimatedTable, { ColumnDef } from "../AnimatedTable";
import apiService from "../../../../service/Api";
import type { IEquipment } from "../../../../types/equipment";

const ROWS_PER_PAGE = 10;

interface AllocationsTabProps {
  equipment: IEquipment;
}

interface GearSetItem {
  id: number;
  equipment: number | null;
  equipment_name?: string;
  quantity?: number;
}

interface EquipmentAllocationApiItem {
  id: number;
  equipment: number | null;
  equipment_name?: string;
  gearset?: {
    id: number;
    name: string;
    items?: GearSetItem[];
  } | null;
  stream?: {
    id: number;
    title: string;
    date: string;
    booking?: {
      booking_type?: string;
      service?: string;
    };
  } | null;
  allocation_date: string;
  return_status: string;
  allocated_to_name?: string;
  allocated_to_lastname?: string;
}

interface AllocationRow {
  id: number;
  event: string;
  type: string;
  date: string;
  crew: string;
  status: string;
}

export default function AllocationsTab({ equipment }: AllocationsTabProps) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [allocPage, setAllocPage] = useState(0);
  const [allocations, setAllocations] = useState<AllocationRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAllocations = useCallback(async () => {
    if (!equipment?.id) return;

    try {
      setLoading(true);
      setError(null);

      const response = await apiService.get<{
        results: EquipmentAllocationApiItem[];
      }>("/api/equipments/equipment-allocations/");

      const equipmentId = Number(equipment.id);

      const rows: AllocationRow[] = response.data.results
        .filter((item) => {
          const directlyAllocated = item.equipment === equipmentId;

          const allocatedThroughGearSet =
            item.gearset?.items?.some(
              (gearItem) => gearItem.equipment === equipmentId
            ) ?? false;

          return directlyAllocated || allocatedThroughGearSet;
        })
        .map((item) => {
          const crewName = [
            item.allocated_to_name,
            item.allocated_to_lastname,
          ]
            .filter(Boolean)
            .join(" ");

          return {
            id: item.id,
            event: item.stream?.title || "—",
            type:
              item.stream?.booking?.booking_type ||
              item.stream?.booking?.service ||
              "—",
            date:
              item.stream?.date ||
              item.allocation_date?.split("T")[0] ||
              "—",
            crew: crewName || "—",
            status: item.return_status || "—",
          };
        });

      setAllocations(rows);
    } catch (err) {
      console.error(err);
      setError("Failed to load allocations.");
      setAllocations([]);
    } finally {
      setLoading(false);
    }
  }, [equipment?.id]);

  useEffect(() => {
    fetchAllocations();
  }, [fetchAllocations]);

  const chipSx = {
    bgcolor: colors.neutral[700],
    color: theme.palette.text.primary,
    fontWeight: 600,
    borderRadius: "16px",
    textTransform: "capitalize",
  };

  const allocationColumns: ColumnDef<AllocationRow>[] = [
    {
      key: "event",
      label: "Event",
      cellSx: { fontWeight: 500, color: theme.palette.text.primary },
      render: (row) => row.event,
    },
    { key: "type", label: "Type", render: (row) => row.type },
    { key: "date", label: "Date", render: (row) => row.date },
    { key: "crew", label: "Crew", render: (row) => row.crew },
    {
      key: "status",
      label: "Status",
      render: (row) => <Chip label={row.status} size="small" sx={chipSx} />,
    },
  ];

  const totalPages = Math.ceil(allocations.length / ROWS_PER_PAGE) || 1;

  const paginatedRows = allocations.slice(
    allocPage * ROWS_PER_PAGE,
    allocPage * ROWS_PER_PAGE + ROWS_PER_PAGE
  );

  return (
    <Box
      sx={{
        bgcolor: theme.palette.background.paper,
        borderRadius: "16px",
        p: 3,
      }}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <AnimatedTable
          columns={allocationColumns}
          rows={paginatedRows}
          getRowId={(row) => row.id}
          page={allocPage}
          totalPages={totalPages}
          onPageChange={setAllocPage}
          emptyMessage="No allocations found for this equipment."
        />
      )}
    </Box>
  );
}